import { apiFetch } from './api';
import { fetchStorePaymentMethods } from './paymentApi';
import { getWilayaName, parseStateCityLabels } from '../data/algeriaGeo';
import { findCountryByIso } from '../data/countries';
import { ECOTRACK_CARRIERS } from '../data/ecotrackCarriers';

/** Local UI canal values → API channel slug */
export const CANAL_TO_CHANNEL_SLUG = {
  kiostore: 'web',
  kiopos: 'pos',
  kioagent: 'chatbot',
};

const CHANNEL_SLUG_TO_CANAL = Object.fromEntries(
  Object.entries(CANAL_TO_CHANNEL_SLUG).map(([canal, slug]) => [slug, canal])
);

/** Slug API depuis order_channel (objet ou string). */
export function resolveOrderChannelSlug(order, fallback = 'web') {
  const ch = order?.order_channel;
  if (typeof ch === 'string') return ch.toLowerCase();
  if (ch?.slug) return String(ch.slug).toLowerCase();
  if (ch?.name) {
    const name = String(ch.name).toLowerCase();
    if (name === 'web') return 'web';
    if (name === 'pos') return 'pos';
    if (name === 'chatbot' || name.includes('agent')) return 'chatbot';
  }
  return String(fallback ?? 'web').toLowerCase();
}

export function channelSlugToCanal(slug) {
  return CHANNEL_SLUG_TO_CANAL[String(slug ?? 'web').toLowerCase()] ?? 'kiostore';
}

/**
 * Acquisition sources — mirror of the backend OrderSource enum (Order.source).
 *
 * Where the CUSTOMER came from, NOT which system recorded the order: that is
 * the canal above (kiostore / kiopos / kioagent → order_channel). A TikTok ad
 * can land in any of them, so neither is derived from the other.
 *
 * Closed vocabulary on purpose — the API answers 400 on anything else, and the
 * per-source analytics group on these exact values. Labels live in i18n under
 * `source_<value>`, never here.
 */
export const ORDER_SOURCES = [
  'unknown', 'facebook', 'instagram', 'tiktok', 'snapchat', 'whatsapp',
  'messenger', 'youtube', 'google', 'phone', 'showroom', 'referral', 'other',
];

/** Orders created before the field existed, and every caller that omits it. */
export const DEFAULT_ORDER_SOURCE = 'unknown';

export function resolveOrderSource(value) {
  const slug = String(value ?? '').toLowerCase();
  return ORDER_SOURCES.includes(slug) ? slug : DEFAULT_ORDER_SOURCE;
}

const CHANNELS_LS_KEY = 'kio_channels';

let channelsCache = null;
let channelsFetchPromise = null;

function readChannelsFromStorage() {
  try {
    const raw = localStorage.getItem(CHANNELS_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeChannelsToStorage(channels) {
  try {
    localStorage.setItem(CHANNELS_LS_KEY, JSON.stringify(channels));
  } catch { /* ignore quota */ }
}

export function getCachedChannels() {
  if (channelsCache?.length) return channelsCache;
  const stored = readChannelsFromStorage();
  if (stored?.length) {
    channelsCache = stored;
    return channelsCache;
  }
  return [];
}

export async function fetchChannels({ force = false } = {}) {
  if (!force && channelsCache?.length) return channelsCache;

  if (!force) {
    const stored = readChannelsFromStorage();
    if (stored?.length) {
      channelsCache = stored;
      return channelsCache;
    }
  }

  if (channelsFetchPromise) return channelsFetchPromise;

  channelsFetchPromise = apiFetch('/channels/?format=json')
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error('channels_fetch_failed'))))
    .then((data) => {
      channelsCache = Array.isArray(data) ? data : (data.results ?? []);
      writeChannelsToStorage(channelsCache);
      return channelsCache;
    })
    .finally(() => {
      channelsFetchPromise = null;
    });

  return channelsFetchPromise;
}

/**
 * Drop the cached channels (memory + localStorage). The /channels/ endpoint is
 * scoped to the active store via the session, so the cache must be cleared when
 * the store changes — otherwise a stale channel id from the previous store is
 * sent on order creation and the backend answers 404 "No Channel matches".
 */
export function clearChannelsCache() {
  channelsCache = null;
  channelsFetchPromise = null;
  try {
    localStorage.removeItem(CHANNELS_LS_KEY);
  } catch { /* ignore */ }
}

export async function channelIdBySlug(slug) {
  const channels = await fetchChannels();
  return channels.find((c) => c.slug === slug)?.id ?? null;
}

const BULK_ACTIONS = new Set(['confirm', 'cancel']);

/**
 * Bulk confirm or cancel orders.
 *
 * `action` is REQUIRED and always sent as ?action=. Omit it and the backend
 * falls back to guessing from the orders' current status — a guess that is
 * wrong for the entire Pré-expédition step, whose rows are still `draft` and
 * therefore read as "confirm". Pressing « Annuler » there confirmed the orders
 * and pushed them to the carrier instead of cancelling them. Throwing on a
 * missing action stops that from coming back unnoticed.
 */
export async function bulkUpdateOrderStatus(slug, orderIds, action) {
  if (!slug || !Array.isArray(orderIds) || !orderIds.length) return null;
  if (!BULK_ACTIONS.has(action)) {
    throw new Error(`bulk_action_required (got: ${action})`);
  }

  const payload = orderIds.map((id) => ({ id }));
  const res = await apiFetch(`/store/${slug}/orders/bulk/?action=${action}`, {
    method: 'PUT',
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const error = await res.text().catch(() => '');
    throw new Error(error || 'bulk_status_failed');
  }

  return res.json();
}

/**
 * Dispatch orders to a staff queue — fills Order.assigned_to.
 *
 * `staff` is a staff uid, 'auto' (round-robin over the store's web team) or
 * 'none' (back to the shared pool).
 *
 * `filterParams` switches to whole-queue mode: the backend re-runs the same
 * OrderFilter query and dispatches everything matching, body ignored. That is
 * the only way to reach a backlog no page-by-page selection can cover.
 *
 * Separate from bulkUpdateOrderStatus on purpose: dispatching is not an order
 * event — no status change, no parcel, no background task, so nothing to poll.
 */
export async function assignOrders(slug, orderIds, staff, filterParams = null) {
  if (!slug || !staff) return null;
  if (!filterParams && (!Array.isArray(orderIds) || !orderIds.length)) return null;

  const params = filterParams
    ? buildOrdersFilterQuery({ ...filterParams, page: 1, pageSize: 1 })
    : new URLSearchParams();
  params.set('staff', staff);
  if (filterParams) params.set('all', 'true');

  const res = await apiFetch(`/store/${slug}/orders/bulk-assign/?${params}`, {
    method: 'PUT',
    body: JSON.stringify(filterParams ? [] : orderIds.map((id) => ({ id }))),
  });

  if (!res.ok) {
    const error = await res.text().catch(() => '');
    throw new Error(error || 'bulk_assign_failed');
  }

  return res.json();
}

/**
 * Self-serve dispatch — the signed-in staff takes the rows he selected out of
 * the shared pool. The pull counterpart of assignOrders (the owner's push).
 *
 * Answers { claimed, requested, remaining, order_ids }; claimed < requested
 * simply means someone else got there first, which is normal and not an error.
 */
export async function claimOrders(slug, orderIds) {
  const ids = Array.isArray(orderIds) ? orderIds.filter(Boolean) : [];
  if (!slug || !ids.length) return null;
  const res = await apiFetch(`/store/${slug}/orders/claim/`, {
    method: 'PUT',
    body: JSON.stringify({ order_ids: ids }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(text || 'claim_failed');
    try {
      err.apiError = JSON.parse(text)?.error ?? null;
    } catch {
      err.apiError = null;
    }
    throw err;
  }
  return res.json();
}

// Single check on a bulk task's state — no retry loop. The task may still be
// running (`ready: false`, e.g. state PENDING/RETRY); callers treat that as
// "still processing in the background" rather than waiting it out.
export async function fetchBulkOrderTask(slug, taskId) {
  if (!slug || !taskId) return null;

  const res = await apiFetch(`/store/${slug}/tasks/${taskId}/`, { noCache: true });
  if (!res.ok) {
    const error = await res.text().catch(() => '');
    throw new Error(error || 'bulk_task_poll_failed');
  }

  return res.json();
}

const PAYMENT_METHOD_MAP = {
  Cash: 'cod',
  'CIB Standard': 'cib',
  CIB: 'cib',
};

// Maps a shipment origin (carrier apiSlug, e.g. 'dhd', '48hr', 'navexdelivery')
// to the display name used as the logo key in Orders.js (LIVRAISON_LOGOS).
// Direct integrations first, then every Ecotrack carrier (apiSlug → id) so
// acronym carriers (DHD, 48HR, World Express…) resolve to their exact logo key.
const ORIGIN_TO_LIVRAISON = {
  yalidine: 'Yalidine',
  noest: 'Noest',
  grostan: 'Grostan',
  zrexpress: 'Zr Express',
  ...Object.fromEntries(
    ECOTRACK_CARRIERS.flatMap((c) => [c.apiSlug, ...(c.backendSlugAliases ?? c.aliases ?? [])].map((k) => [k, c.id]))
  ),
};

const CONTACT_STATUS_TO_LOCAL = {
  NONE: 'a_confirmer',
  NO_RESPONSE_1: 'pas_rep_1',
  NO_RESPONSE_2: 'pas_rep_2',
  NO_RESPONSE_3: 'pas_rep_3',
  UNREACHABLE: 'injoignable',
  BUSY: 'occupe',
  VOICEMAIL: 'boite_vocale',
  WHATSAPP_FAIL: 'no_whatsapp',
  NO_RESPONSE_SMS: 'no_sms',
  CONFIRMED: 'confirme',
  REFUSED: 'annule',
};

function formatOrderDateFr(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('fr-DZ', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function formatPhone(raw) {
  if (!raw) return '';
  const cleaned = String(raw).replace(/,$/, '').trim();
  if (!cleaned) return '';

  let digits = cleaned.replace(/\D/g, '');
  if (!digits) return cleaned;

  // Numéros algériens sans indicatif (ex. 0782865848, 782865848)
  if (!cleaned.startsWith('+') && !digits.startsWith('213')) {
    if (digits.startsWith('0') && digits.length >= 9) {
      digits = `213${digits.slice(1)}`;
    } else if (digits.length >= 9 && /^[5677]/.test(digits)) {
      digits = `213${digits}`;
    }
  } else if (digits.startsWith('2130')) {
    digits = `213${digits.slice(4)}`;
  }

  if (digits.startsWith('213') && digits.length >= 12) {
    const local = digits.slice(3);
    const formatted = `${local.slice(0, 4)} ${local.slice(4, 6)} ${local.slice(6, 8)} ${local.slice(8)}`.trim();
    return `+213 ${formatted}`;
  }

  if (digits.startsWith('213')) {
    return `+${digits}`;
  }

  return cleaned.startsWith('+') ? cleaned : `+${digits}`;
}

/** Chiffres E.164 sans + pour wa.me (ex. 213782865848) */
export function phoneDigitsForWa(raw) {
  if (!raw) return '';
  let d = String(raw).replace(/\D/g, '');
  if (d.startsWith('2130')) d = `213${d.slice(4)}`;
  else if (d.startsWith('0') && d.length >= 9) d = `213${d.slice(1)}`;
  else if (!d.startsWith('213') && d.length >= 9 && /^[5677]/.test(d)) d = `213${d}`;
  return d;
}

/** Contact pour invitation client : e-mail invité en priorité, sinon téléphone E.164. */
export function resolveOrderInviteContact(order) {
  const email = String(order?.guestEmail ?? order?.email ?? '').trim();
  if (email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return { email, phoneNumber: null, channel: 'email' };
  }
  const digits = phoneDigitsForWa(order?.phone);
  const phoneNumber = digits ? `+${digits}` : null;
  return { email: null, phoneNumber, channel: phoneNumber ? 'phone' : null };
}

/** Commande invité : customer null + auxiliary.guest renseigné */
function isGuestOrder(o) {
  const guest = o.auxiliary?.guest;
  return !o.customer && guest != null && typeof guest === 'object' && Object.keys(guest).length > 0;
}

function guestShippingAddress(guest, orderAddresses) {
  const list = Array.isArray(orderAddresses) ? orderAddresses : [];
  const fromOrder = list.find((a) => a.purpose === 'shipping') ?? null;
  if (fromOrder) return fromOrder;
  if (guest?.shipping_address && Object.keys(guest.shipping_address).length) {
    return guest.shipping_address;
  }
  if (Array.isArray(guest?.addresses) && guest.addresses.length) {
    return guest.addresses[0];
  }
  return null;
}

/** Aligné neo-store-dashboard : client → expédition → invité (priorité invité si pas de client) */
function resolveOrderPhone(o) {
  const guest = o.auxiliary?.guest;
  const customer = o.customer;
  const ship = o.shipment_info;
  const shipInfo = ship?.info ?? ship;

  const fromShip = ship
    ? (
      ship.contact_phone
      ?? ship.telephone
      ?? ship.phone_number
      ?? ship.phone
      ?? ship.customer?.phone?.number1
      ?? shipInfo?.contact_phone
      ?? shipInfo?.telephone
      ?? shipInfo?.phone_number
      ?? shipInfo?.phone
      ?? ''
    )
    : '';

  const raw = customer?.user?.phone_number
    ?? (!customer ? guest?.phone_number : null)
    ?? fromShip
    ?? guest?.phone_number
    ?? '';

  return String(raw).replace(/,$/, '').trim();
}

function livraisonFromShipment(ship) {
  if (!ship) return '—';
  const origin = String(ship.origin ?? '').toLowerCase();
  for (const [key, label] of Object.entries(ORIGIN_TO_LIVRAISON)) {
    if (origin.includes(key)) return label;
  }
  if (!origin) return '—';
  return origin.charAt(0).toUpperCase() + origin.slice(1);
}

/**
 * All the variant values for a line item, joined: "xl, gris".
 * Copes with the shapes the API uses across endpoints:
 *   [{ option, value }]            — orders-filter / order detail
 *   ["xl", "gris"]                 — plain strings
 *   { taille: "xl", couleur: … }   — option → value map
 *   "xl / gris"                    — already-formatted string
 */
function variantLabelFromAttributes(attributes) {
  if (attributes == null) return '';
  if (typeof attributes === 'string') return attributes.trim();

  const list = Array.isArray(attributes)
    ? attributes
    : Object.entries(attributes).map(([option, value]) => ({ option, value }));

  return list
    .map((a) => (a && typeof a === 'object'
      ? (a.value ?? a.name ?? a.label ?? a.option_value ?? '')
      : a))
    .map((v) => (v == null ? '' : String(v).trim()))
    .filter(Boolean)
    .join(' ');
}

/** Pick the richest variant label available on a raw line item. */
function variantLabelFromItem(item) {
  const candidates = [
    variantLabelFromAttributes(item.attributes),
    variantLabelFromAttributes(item.variant_attributes),
    variantLabelFromAttributes(item.options),
    typeof item.variant_name === 'string' ? item.variant_name : '',
    typeof item.variant_title === 'string' ? item.variant_title : '',
  ];
  return candidates.reduce((best, cur) => (cur && cur.length > best.length ? cur : best), '');
}

function itemsFromApiOrder(o) {
  const itemsList = o.items_detail?.length ? o.items_detail : (o.items?.length ? o.items : null);
  if (itemsList) {
    return itemsList.map((item) => ({
      name: item.name ?? item.product_name,
      qty: item.ordered_quantity ?? item.quantity ?? 1,
      image: item.src ?? null,
      tax: item.tax ?? '0.00',
      attributes: Array.isArray(item.attributes) ? item.attributes : [],
      variant: variantLabelFromItem(item),
      _itemId: item.id ?? null,
      _variantId: item.variant_id ?? null,
      _productId: item.product_id ?? null,
      _productSlug: item.product_slug ?? null,
    }));
  }
  const ship = o.shipment_info;
  if (ship?.products?.length) {
    return ship.products.map((p) => ({
      name: p.name,
      qty: parseFloat(p.quantity) || 1,
      image: null,
    }));
  }
  if (ship?.produit || ship?.product_name) {
    return [{ name: ship.produit || ship.product_name, qty: parseFloat(ship.quantite ?? ship.qte) || 1, image: null }];
  }
  return [{ name: '—', qty: 1, image: null }];
}

export const LOCAL_TO_CONTACT_STATUS = Object.fromEntries(
  Object.entries(CONTACT_STATUS_TO_LOCAL).map(([k, v]) => [v, k])
);
export const LOCAL_TO_API_STATUS = { confirme: 'confirmed', annule: 'canceled' };
/** API status when confirming a draft order (moves to preparation pipeline) */
export const API_ORDER_STATUS_UNFULFILLED = 'unfulfilled';
export const API_ORDER_STATUS_CANCELED = 'canceled';
/** API status for an order back in the confirmation phase (a contact status is set, not confirmed/cancelled) */
export const API_ORDER_STATUS_DRAFT = 'draft';
export const LOCAL_TO_PAYMENT_METHOD = { cod: 'Cash', cib: 'CIB' };

export function mapApiOrderStatus(o) {
  if (o.status === 'canceled') return 'annule';
  if (o.status === 'confirmed' || o.status === 'unfulfilled') return 'confirme';
  return CONTACT_STATUS_TO_LOCAL[o.contact_status] ?? 'a_confirmer';
}

export function mapApiOrderToLocal(o, channelSlug = 'web') {
  const guest = o.auxiliary?.guest;
  const customer = o.customer;
  const guestOrder = isGuestOrder(o);
  let recipient = 'Invité';
  let clientCategory = null;

  if (customer?.user) {
    recipient = `${customer.user.first_name ?? ''} ${customer.user.last_name ?? ''}`.trim() || '—';
    const cs = customer.customer_status;
    clientCategory = cs === 'VIP' ? 'vip' : cs === 'NEW' ? 'new' : 'normal';
  } else if (guestOrder && guest) {
    recipient = `${guest.first_name ?? ''} ${guest.last_name ?? ''}`.trim() || 'Invité';
  }

  const phone = resolveOrderPhone(o);

  const products = itemsFromApiOrder(o);
  const first = products[0];
  const ship = o.shipment_info;
  const orderAddresses = o.order_addresses ?? o.order_address ?? [];
  const shipAddr = guestShippingAddress(guest, orderAddresses);

  const parsedStateCity = parseStateCityLabels(o.state_city ?? []);
  let commune = parsedStateCity.commune || ship?.commune || ship?.commune_name || '—';
  let wilaya = parsedStateCity.wilaya || ship?.wilaya || ship?.wilaya_name || '—';

  if (shipAddr) {
    const stateId = shipAddr.state?.id ?? shipAddr.state;
    if (stateId != null) {
      wilaya = getWilayaName(stateId) || wilaya;
    }
    if (typeof shipAddr.city === 'object' && shipAddr.city != null) {
      const cityName = shipAddr.city.name ?? shipAddr.city.label ?? '';
      if (cityName) commune = cityName;
    }
  }

  const pickup = ship?.stop_desk === 1
    || ship?.stopdesk_status === true
    || ship?.type === 'pickup'
    || ship?.type === 1 && ship?.stop_desk === 1;

  const pm = (o.payment_method ?? [])[0] ?? '';
  const paiement = PAYMENT_METHOD_MAP[pm]
    ?? (String(pm).toLowerCase().includes('cib') ? 'cib' : 'cod');

  const paiementStatus = paiement === 'cib'
    ? (o.payment === 'PAID' || o.payment === 'SUCCESS' ? 'reussi' : 'echoue')
    : 'n_a';

  return {
    id: o.id,
    name: `#${o.sequence}`,
    sequence: o.sequence ?? null,
    date: formatOrderDateFr(o.created),
    dateISO: o.created?.slice(0, 10) ?? '',
    createdAt: o.created ?? null,
    recipient,
    guest: guestOrder,
    guestFirstName: guestOrder ? (guest?.first_name ?? '') : '',
    guestLastName: guestOrder ? (guest?.last_name ?? '') : '',
    clientCategory,
    product: first?.name ?? '—',
    qty: products.reduce((sum, p) => sum + p.qty, 0) || 1,
    products,
    pays: 'DZ',
    wilaya,
    commune,
    phone: formatPhone(phone),
    tracking: ship?.tracking ?? null,
    carrierOrigin: ship?.origin ?? null,
    paiement,
    livraison: livraisonFromShipment(ship),
    canal: channelSlugToCanal(resolveOrderChannelSlug(o, channelSlug)),
    orderChannelSlug: resolveOrderChannelSlug(o, channelSlug),
    orderChannelId: o.order_channel?.id ?? null,
    source: resolveOrderSource(o.source),
    // Single-product landing page the order came from: { id, name, slug } or null.
    funnel: o.funnel ?? null,
    typelivraison: pickup ? 'pickup' : 'domicile',
    codePromo: null,
    status: mapApiOrderStatus(o),
    apiOrderStatus: o.status ?? null,
    total: resolveGrossTotal(o),
    netTotal: o.net_total_field ?? o.net_total ?? o.total_products ?? null,
    pipelineStep: 0,
    paiementStatus,
    customerId: customer?.id ?? null,
    address: shipAddr?.street ?? ship?.street ?? guest?.shipping_address?.street ?? '',
    // customer_note: the note given at checkout (storefront form, KIOAGENT chatbot).
    note: ship?.remarque || o.auxiliary?.note || o.customer_note || o.note || '',
    guestEmail: o.auxiliary?.guest?.email ?? '',
    shipmentId: ship?.id ?? null,
    reduction_code: o.reduction_code ?? null,
    reduction_total: o.reduction_total != null ? parseFloat(o.reduction_total) : 0,
    remise: resolveRemiseFromApi(o),
    servedBy: resolveServedByFromApi(o),
    // Dispatch queue owner. Distinct from servedBy, which is whoever acted on
    // the order; this is whoever owes the confirmation call.
    assignedTo: o.assigned_to
      ? { uid: o.assigned_to.uid, name: o.assigned_to.staff_name || '' }
      : null,
    fulfillement: o.fulfillement ?? o.shipping_status ?? 'UNFULFILLED',
    // Raw carrier/shipment status. Already normalized to a Shipment.STATUS id by
    // the backend, except for Yalidine / grostan where Order.shipping_status
    // returns the carrier's own label (info.last_status).
    shippingStatus: o.shipping_status ?? null,
    montantPaye: resolveMontantPayeFromApi(o),
    paymentStatus: o.payment ?? null,
    refunded: isOrderFullyRefundedFromApi(o),
    _api: true,
  };
}

/** Map API fulfillement → preparation tab filter id */
export function mapApiFulfilmentToPrepTab(fulfillement) {
  const upper = String(fulfillement ?? 'UNFULFILLED').toUpperCase();
  if (upper === 'FULFILLED') return 'traite';
  if (upper === 'PARTIALLY_FULFILLED' || upper === 'PARTIAL') return 'partiel';
  return 'non_traite';
}

/** Map API order.payment → encaissement tab id */
export function mapApiPaymentToEncaissement(payment) {
  const upper = String(payment ?? '').toUpperCase();
  if (upper === 'REFUNDED') return 'rembourse';
  if (upper === 'PAID' || upper === 'SUCCESS') return 'paye';
  if (upper === 'PARTIALLY_PAID' || upper === 'PARTIAL') return 'partiel';
  if (upper === 'PENDING' || upper === 'UNPAID' || upper === 'NOT_PAID') return 'non_paye';
  return null;
}

export function isOrderPaymentRefunded(payment) {
  return String(payment ?? '').toUpperCase() === 'REFUNDED';
}

/**
 * True when an order is fully refunded. The API keeps `payment` = "PAID" after a
 * refund, so the real signal is the `refunds` array plus the per-item `to_refund`
 * field (= quantity still refundable). Fully refunded ⇔ has refunds and nothing
 * left to refund.
 */
export function isOrderFullyRefundedFromApi(o) {
  const refundsArr = Array.isArray(o?.refunds) ? o.refunds : [];
  if (refundsArr.length === 0) return false;
  const rawItems = o.items_detail?.length ? o.items_detail : (o.items ?? []);
  if (!rawItems.length) return false;

  const hasToRefund = rawItems.some((it) => it.to_refund != null && it.to_refund !== '');
  if (hasToRefund) {
    const toRefundTotal = rawItems.reduce((s, it) => s + (Number(it.to_refund) || 0), 0);
    return toRefundTotal === 0;
  }

  const orderedTotal = rawItems.reduce((s, it) => s + orderedQtyFromItem(it), 0);
  const refundedTotal = refundsArr.reduce((s, r) =>
    s + (r.refund_items ?? []).reduce((ss, ri) => ss + (Number(ri.quantity) || 0), 0), 0);
  return orderedTotal > 0 && refundedTotal >= orderedTotal;
}

/**
 * POS discounts are recorded as custom line items rather than a promo
 * code/reduction_total — a percentage discount reads e.g. `{ name: "Remise
 * 15.0%", price: -750 }`, a flat one `{ name: "Remise 20.00", price: -20 }`.
 * Displayed as the name with the "Remise" word stripped (e.g. "15.0%" /
 * "20.00") rather than the price — the name already carries the meaningful
 * value, whether percentage or flat amount. Only items actually named
 * "Remise…" count — custom_items can carry other, unrelated line items (fees,
 * manual additions). Multiple discount lines are joined with a comma.
 */
function resolveRemiseFromApi(o) {
  const items = Array.isArray(o.custom_items) ? o.custom_items : [];
  return items
    .filter((it) => /remise/i.test(it?.name ?? ''))
    .map((it) => String(it.name).replace(/remise/i, '').trim())
    .filter(Boolean)
    .join(', ');
}

/**
 * Staff who served/sold the order (order_served_by, see currentStaffUid) —
 * name for display. Shape isn't pinned down yet, so this reads defensively:
 * a nested staff object (staff_name, first/last name) or a plain string.
 */
function resolveServedByFromApi(o) {
  const sb = o.order_served_by ?? o.served_by;
  if (!sb) return null;
  if (typeof sb === 'string') return sb;
  if (typeof sb === 'object') {
    if (sb.staff_name) return sb.staff_name;
    const full = `${sb.first_name ?? ''} ${sb.last_name ?? ''}`.trim();
    if (full) return full;
    if (sb.name) return sb.name;
  }
  return null;
}

// POS rows (orders-filter step=pos) may omit gross_total_field, which left the
// Total column at 0. Fall back only when a field is ABSENT: a present 0 is a
// legitimate free order and must stay 0.
function resolveGrossTotal(o) {
  for (const v of [o.gross_total_field, o.gross_total, o.net_total_field, o.net_total]) {
    if (v == null || v === '') continue;
    const n = Number(v);
    if (Number.isFinite(n)) return n;
  }
  return 0;
}

function resolveMontantPayeFromApi(o) {
  const total = resolveGrossTotal(o);
  const explicit = Number(o.paid ?? o.paid_total ?? o.paid_amount ?? 0);
  if (Number.isFinite(explicit) && explicit > 0) return explicit;

  const fromPayment = mapApiPaymentToEncaissement(o.payment);
  if (fromPayment === 'paye') return total;
  return 0;
}

/** fulfillement query param for preparation status tabs */
export function preparationFulfilmentParam(tabStatus) {
  if (tabStatus === 'non_traite') return 'UNFULFILLED';
  if (tabStatus === 'partiel') return 'PARTIALLY_FULFILLED';
  if (tabStatus === 'traite') return 'FULFILLED';
  return '';
}

/**
 * Status query params for confirmation step tabs.
 *
 * Confirmation only owns the drafts that are NOT confirmed yet: a draft whose
 * contact_status is CONFIRMED now belongs to Pré-expédition (see
 * buildConfirmationOrdersFilterQuery / buildExpeditionOrdersFilterQuery), so
 * the two steps are disjoint.
 */
export function confirmationStatusParams(tabStatus) {
  if (!tabStatus) return ['draft'];
  if (tabStatus === 'annule') return ['canceled'];
  // 'confirme' falls through to draft: the tab filters on
  // contact_status=CONFIRMED, i.e. the drafts Pré-expédition now owns.
  return ['draft'];
}

const DATE_QUICK_RANGE_MAP = {
  today:      '24h',
  yesterday:  'yesterday',
  last7:      '7d',
  last30:     '30d',
  this_month: '30d',
};

function isoDate(d) {
  return d.toISOString().slice(0, 10);
}

export function buildOrdersFilterQuery({
  orderChannel,
  page,
  pageSize,
  step,
  statuses = [],
  confirmedStatus = '',
  fulfillement = '',
  shipmentStatus = '',
  products = [],
  contactStatuses = [],
  sources = [],
  funnel = '',
  carriers = [],
  dateFilter = '',
  customDateRange = {},
  search = '',
  excludeChannels = [],
  excludeContactStatuses = [],
  excludeStatuses = [],
  excludeShipmentStatuses = [],
  isConfirmed = null,
  assignedTo = '',
}) {
  const params = new URLSearchParams();
  if (orderChannel) params.set('order_channel', orderChannel);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));
  // Dispatch queue. 'unassigned' is the shared pool (assigned_to IS NULL), any
  // other value is a staff uid. Applied in both branches so a search inside a
  // queue stays inside that queue.
  if (assignedTo === 'unassigned') {
    params.set('unassigned', 'true');
  } else if (assignedTo) {
    params.set('assigned_to', assignedTo);
  }
  if (search.trim()) {
    params.set('page_size', '1000');
    params.set('search', search.trim());
    if (isConfirmed != null) {
      // is_confirmed replaces the status/contact_status pair entirely: it is a
      // single server-side predicate, so forcing status=draft below would
      // shrink it back to the drafts only.
    } else if (confirmedStatus) {
      params.set('confirmed_status', confirmedStatus);
    } else if (fulfillement) {
      params.set('fulfillement', fulfillement);
    } else if (shipmentStatus) {
      params.set('shipment_info_last_status', shipmentStatus);
    } else {
      statuses.forEach((s) => params.append('status', s));
      if (!statuses.length) params.append('status', 'draft');
    }
    contactStatuses.forEach((cs) => params.append('contact_status', cs));
    if (step) params.set('step', step);
  } else {
    carriers.forEach((c) => params.append('carrier_name', c));
    if (dateFilter && dateFilter !== 'personnalise') {
      const qr = DATE_QUICK_RANGE_MAP[dateFilter];
      if (qr) params.set('quick_range', qr);
    } else if (dateFilter === 'personnalise' && customDateRange?.start) {
      params.set('date_from', isoDate(customDateRange.start));
      if (customDateRange.end) params.set('date_to', isoDate(customDateRange.end));
    }
    if (confirmedStatus) {
      params.set('confirmed_status', confirmedStatus);
    }
    if (fulfillement) {
      params.set('fulfillement', fulfillement);
    }
    if (shipmentStatus) {
      params.set('shipment_info_last_status', shipmentStatus);
    }
    if (!confirmedStatus && !fulfillement && !shipmentStatus
        && isConfirmed == null) {
      statuses.forEach((s) => params.append('status', s));
    }
    contactStatuses.forEach((cs) => params.append('contact_status', cs));
    products.forEach((p) => params.append('product', p));
    if (step) params.set('step', step);
  }
  // Population exclusions — they apply to BOTH branches above, because a global
  // search must stay scoped to the same population as the normal listing.
  // `exclude_channel` drops orders whose channel slug is listed while KEEPING
  // orders with a NULL channel; together with `exclude_contact_status` these
  // two replace the old "pin the funnel to the web channel id" trick, which
  // silently dropped chatbot/NULL-channel orders the analytics do count.
  if (excludeChannels.length) params.set('exclude_channel', excludeChannels.join(','));
  if (excludeContactStatuses.length) {
    params.set('exclude_contact_status', excludeContactStatuses.join(','));
  }
  if (excludeStatuses.length) params.set('exclude_status', excludeStatuses.join(','));
  if (excludeShipmentStatuses.length) params.set('exclude_shipment_status', excludeShipmentStatuses.join(','));
  // Acquisition source — multi-valued, ORed server-side. Also outside the two
  // branches: a source drill-down must survive a global search.
  sources.forEach((s) => params.append('source', s));
  // Landing page (funnels app) the order came from — same scope as the source.
  if (funnel) params.set('funnel', funnel);
  if (isConfirmed != null) params.set('is_confirmed', String(isConfirmed));
  params.set('format', 'json');
  return params;
}

/**
 * Channel exclusion shared by every FUNNEL step (Confirmation, Pré-expédition,
 * Préparation, Retourné, Livré). Their population is "every channel except
 * POS", exactly like the funnel analytics: a POS sale is created confirmed,
 * paid and fulfilled at the register and owns its own step.
 *
 * An explicit caller-supplied `orderChannel` (the user picking a canal filter)
 * wins — the query is already pinned to a single channel, so there is nothing
 * left to exclude. An `excludeChannels` the caller passed is always preserved.
 *
 * buildPosOrdersFilterQuery deliberately does NOT use this: it is channel-locked
 * to POS already.
 */
function funnelChannelExclusion(options) {
  const caller = options.excludeChannels ?? [];
  if (options.orderChannel) return caller;
  return caller.includes('pos') ? caller : [...caller, 'pos'];
}

/**
 * Status exclusion shared by the two SHIPMENT steps (Retourné, Livré). Both are
 * worklists, and a canceled order has nothing left to work on.
 *
 * The backend no longer overwrites `Shipment.status` with `canceled` when the
 * order is canceled — a settled `shipped`/`returned` now survives cancellation,
 * because that overwrite was destroying the delivery-rate history (a month with
 * 4 delivered / 1 canceled return read 4/0 = 100%). The parcel really was
 * delivered/returned, so the analytics keep counting it in `total_orders` (the
 * rate's denominator = history); the LIST must drop it explicitly, otherwise
 * canceled parcels sit in the step forever. That worklist population is what
 * `total_to_process` counts, and `exclude_status=canceled` is how the list
 * matches it.
 *
 * An `excludeStatuses` the caller passed is always preserved.
 */
function shipmentStepStatusExclusion(options) {
  const caller = options.excludeStatuses ?? [];
  return caller.includes('canceled') ? caller : [...caller, 'canceled'];
}

/**
 * Confirmation — the drafts NOT confirmed yet; Pré-expédition owns the
 * confirmed ones, so the two steps (and their badges) are disjoint.
 *
 * That default population is what the step badge counts. The two tabs that
 * look BACKWARDS are answered literally instead, each on its own predicate:
 *   - « Annulé » resolves to status=canceled. Most canceled orders were
 *     confirmed first, so the CONFIRMED exclusion must not apply or the tab
 *     would come back empty.
 *   - « Confirmé » lists every order this step ever confirmed — including the
 *     ones that already moved on to Préparation or beyond. It uses the API's
 *     own is_confirmed predicate, the very one the total_confirmed badge is
 *     counted with, so tab and badge cannot disagree. Filtering on
 *     status=draft + contact_status=CONFIRMED instead would silently drop
 *     every order that has since progressed (4 confirmed, 3 listed).
 */
export function buildConfirmationOrdersFilterQuery(options) {
  const tab = options.tabStatus ?? '';

  if (tab === 'confirme') {
    // is_confirmed supersedes the status/contact_status pair; a secondary
    // contact-status filter would contradict it, so it is dropped on purpose.
    return buildOrdersFilterQuery({
      ...options,
      statuses: [],
      contactStatuses: [],
      isConfirmed: true,
      excludeChannels: funnelChannelExclusion(options),
    });
  }

  const isDefaultPopulation = !tab && !(options.contactStatuses ?? []).length;
  return buildOrdersFilterQuery({
    ...options,
    statuses: confirmationStatusParams(tab),
    excludeContactStatuses: isDefaultPopulation ? ['CONFIRMED'] : [],
    excludeChannels: funnelChannelExclusion(options),
  });
}

/** Préparation — confirmed + fulfillement (optionnel selon l’onglet). */
export function buildPreparationOrdersFilterQuery(options) {
  return buildOrdersFilterQuery({
    ...options,
    confirmedStatus: 'confirmed',
    fulfillement: options.fulfillement ?? '',
    step: 'preparation',
    excludeChannels: funnelChannelExclusion(options),
  });
}

/** En expédition — status=draft + contact_status=CONFIRMED (filtre backend dédié). */
export function buildExpeditionOrdersFilterQuery(options) {
  return buildOrdersFilterQuery({
    ...options,
    statuses: ['draft'],
    contactStatuses: ['CONFIRMED'],
    excludeChannels: funnelChannelExclusion(options),
    // A parcel already delivered or returned is not waiting to be handed to a
    // carrier: a returned order then canceled for restock stays draft +
    // CONFIRMED and used to squat this worklist forever.
    excludeShipmentStatuses: SETTLED_SHIPMENT_STATUSES,
  });
}

/**
 * Retourné — colis revenus du transporteur.
 *
 * Le filtre backend s'appelle `shipment_info_last_status`
 * (OrderFilter.filter_shipment_status) et non `shipment_status` : un nom
 * inconnu est ignoré en silence par django-filter et renvoie toute la liste.
 *
 * La valeur est un `Shipment.status` interne : les libellés bruts des
 * transporteurs sont déjà normalisés vers `returned` côté backend
 * (status_maps.py — Yalidine, ZRExpress, Ecotrack, Noest).
 */
export const RETURNED_SHIPMENT_STATUS = 'returned';

export function buildReturnedOrdersFilterQuery(options) {
  return buildOrdersFilterQuery({
    ...options,
    confirmedStatus: '',
    fulfillement: '',
    shipmentStatus: RETURNED_SHIPMENT_STATUS,
    excludeChannels: funnelChannelExclusion(options),
    // Retourné is the RESTOCK worklist: a canceled return is already restocked.
    excludeStatuses: shipmentStepStatusExclusion(options),
  });
}

/** Livré — l'autre issue d'expédition, même filtre que les retours. */
export const DELIVERED_SHIPMENT_STATUS = 'shipped';

export function buildDeliveredOrdersFilterQuery(options) {
  return buildOrdersFilterQuery({
    ...options,
    confirmedStatus: '',
    fulfillement: '',
    shipmentStatus: DELIVERED_SHIPMENT_STATUS,
    excludeChannels: funnelChannelExclusion(options),
    // Livré is the ENCAISSEMENT worklist: cash on a canceled order is not
    // collectable, so it drops out of the list (but stays in total_orders).
    excludeStatuses: shipmentStepStatusExclusion(options),
  });
}

/**
 * Settled parcels — the ones that already reached their destination, either
 * delivered (`shipped`) or sent back (`returned`). The backend refuses to
 * (re)confirm an order whose parcel is settled: it answers with
 * `skipped_orders` / `reason: "settled_parcel"`.
 */
export const SETTLED_SHIPMENT_STATUSES = ['shipped', 'returned'];

/**
 * POS — every order placed through the POS channel, no funnel/status filter.
 *
 * A POS sale is created already confirmed, paid and fulfilled at the register
 * (the backend forces it to FULFILLED — see filter_fulfillement — and it never
 * gets a Shipment row), so it never legitimately sits in Confirmation,
 * Préparation or Livré. This query has no confirmed_status/fulfillement/
 * shipment filter at all: population is scoped entirely by `order_channel`,
 * which the caller must resolve to the store's POS channel id and pass in.
 */
export function buildPosOrdersFilterQuery(options) {
  return buildOrdersFilterQuery({
    ...options,
    confirmedStatus: '',
    fulfillement: '',
    shipmentStatus: '',
    step: 'pos',
  });
}

export const CLIENT_CATEGORY_STATUS_ID = {
  vip:    14097,
  normal: 14096,
  new:    14095,
};

export async function saveApiCustomer(slug, customerId, partialBody) {
  const res = await apiFetch(
    `/store/${slug}/customers/?format=json`,
    { method: 'PUT', body: JSON.stringify({ id: customerId, ...partialBody }) }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`save_customer_failed: ${res.status} ${text}`);
  }
  return res.json();
}

export async function saveApiOrder(slug, orderId, partialBody) {
  const res = await apiFetch(
    `/store/${slug}/orders/?format=json`,
    { method: 'PUT', body: JSON.stringify({ id: orderId, ...partialBody }) }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`save_order_failed: ${res.status} ${text}`);
  }
  return res.json();
}

export function cancelApiOrder(slug, orderId) {
  return saveApiOrder(slug, orderId, { status: API_ORDER_STATUS_CANCELED });
}

export async function deleteApiOrder(slug, orderId) {
  const res = await apiFetch(`/store/${slug}/orders/?format=json`, {
    method: 'DELETE',
    body: JSON.stringify({ id: orderId }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`delete_order_failed: ${res.status} ${text}`);
  }
}

export async function saveApiOrderItem(slug, orderId, item) {
  const res = await apiFetch(
    `/store/${slug}/orders/${orderId}/?format=json`,
    { method: 'PUT', body: JSON.stringify(item) }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`save_order_item_failed: ${res.status} ${text}`);
  }
  return res.json();
}

export async function deleteApiOrderItem(slug, orderId, itemId) {
  const res = await apiFetch(
    `/store/${slug}/orders/${orderId}/?format=json`,
    { method: 'DELETE', body: JSON.stringify({ id: itemId }) }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`delete_order_item_failed: ${res.status} ${text}`);
  }
  return res.status === 204 ? null : res.json().catch(() => null);
}

export async function addApiOrderItems(slug, orderId, items) {
  const res = await apiFetch(
    `/store/${slug}/orders/${orderId}/?format=json`,
    { method: 'POST', body: JSON.stringify(items) }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`add_order_items_failed: ${res.status} ${text}`);
  }
  return res.json();
}

/**
 * uid of the current user's Staff record for a given store, read from the
 * persisted auth user. Used to attribute orders to the staff who created them
 * (order_served_by). Returns null for a plain user / store owner (no staff
 * membership) — the backend then falls back to the session user's staff.
 */
export function currentStaffUid(slug) {
  try {
    const user = JSON.parse(localStorage.getItem('auth_user') || 'null');
    const memberships = (user?.staff || []).filter((s) => s.storeSlug === slug);
    if (!memberships.length) return null;
    const owner = memberships.find((s) => s.role === 'Owner' || s.role === 'owner');
    return (owner ?? memberships[0])?.uid ?? null;
  } catch {
    return null;
  }
}

export async function createApiOrder(slug, body) {
  // Attribute the order to the creating staff when known. Only set on creation
  // (not on partial PUT updates, which would clobber the original server).
  const servedBy = currentStaffUid(slug);
  const payload = (servedBy && body?.order_served_by == null)
    ? { ...body, order_served_by: servedBy }
    : body;
  const res = await apiFetch(
    `/store/${slug}/orders/?format=json`,
    { method: 'POST', body: JSON.stringify(payload) }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`create_order_failed: ${res.status} ${text}`);
  }
  return res.json();
}

export function buildOrderAddressBody({ street, street2, cityId, purpose }) {
  const city = Number(cityId);
  if (!city) {
    throw new Error('Commune requise.');
  }

  return {
    purpose,
    street: String(street ?? '').trim(),
    street2: String(street2 ?? '').trim(),
    city,
  };
}

export async function createApiShipment(slug, body) {
  const res = await apiFetch(`/store/${slug}/shipments/?format=json`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`create_shipment_failed: ${res.status} ${text}`);
  }
  return res.json().catch(() => null);
}

export async function addApiOrderAddress(slug, orderId, addressBody) {
  const res = await apiFetch(
    `/store/${slug}/orders/${orderId}/order-address/?format=json`,
    { method: 'POST', body: JSON.stringify([addressBody]) }
  );
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`add_order_address_failed: ${res.status} ${text}`);
  }
  return res.json();
}

export async function fetchApiOrder(slug, orderId, channelSlug = 'web') {
  const res = await apiFetch(`/store/${slug}/orders/${orderId}/?format=json`);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`fetch_order_failed: ${res.status} ${text}`);
  }
  const data = await res.json();
  return mapApiOrderToLocal(data, channelSlug);
}

export async function fetchOrdersFilter(slug, queryParams, fallbackChannelSlug) {
  const qs = queryParams instanceof URLSearchParams
    ? queryParams
    : buildOrdersFilterQuery(queryParams);
  const res = await apiFetch(`/store/${slug}/orders-filter/?${qs}`);
  if (res.status === 404) return { count: 0, next: null, previous: null, results: [] };
  if (!res.ok) throw new Error('orders_filter_failed');
  const data = await res.json();
  return {
    count: data.count ?? 0,
    next: data.next,
    previous: data.previous,
    results: (data.results ?? []).map((o) => mapApiOrderToLocal(o, fallbackChannelSlug)),
  };
}

function isUuidLike(value) {
  if (value == null || value === '') return false;
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(value).trim());
}

function fulfilledQtyFromItem(item) {
  return Number(
    item.new_fulfilled
    ?? item.fulfilled_quantity
    ?? item.fulfilled
    ?? item.quantity_fulfilled
    ?? 0,
  );
}

function orderedQtyFromItem(item) {
  return Number(item.ordered_quantity ?? item.quantity ?? 0);
}

export function fulfillmentLineKey(item) {
  return String(item.id ?? item._itemId ?? item.name ?? '');
}

export function fulfillmentLinesFromDetail(detail) {
  const rawItems = detail?.items?.length
    ? detail.items
    : (detail?.items_detail?.length ? detail.items_detail : []);
  return rawItems.map((it) => {
    const ordered = orderedQtyFromItem(it);
    const fulfilled = fulfilledQtyFromItem(it);
    const remaining = Math.max(0, ordered - fulfilled);
    return {
      ...it,
      key: fulfillmentLineKey(it),
      name: it.name ?? it.product_name ?? '—',
      ordered,
      fulfilled,
      remaining,
      pending: remaining > 0,
      variantId: it.variant_id ?? it._variantId ?? null,
    };
  });
}

function refundedQtyFromItem(item) {
  const explicit = item.refunded_quantity
    ?? item.quantity_refunded
    ?? item.refunded
    ?? item.refunded_qty;
  if (explicit != null && explicit !== '') return Number(explicit) || 0;
  // The API doesn't expose a refunded count; it exposes `to_refund` = the
  // quantity still refundable. Derive refunded = ordered - to_refund.
  if (item.to_refund != null && item.to_refund !== '') {
    const ordered = orderedQtyFromItem(item);
    const toRefund = Number(item.to_refund) || 0;
    return Math.max(0, ordered - toRefund);
  }
  return 0;
}

function lineUnitPriceFromItem(item) {
  return Number(item.price ?? item.unit_price ?? item.price_field ?? 0);
}

export function lineRefundAmount(line, qty) {
  const unitPrice = Number(line.unitPrice ?? lineUnitPriceFromItem(line) ?? 0);
  const taxPct = Number.parseFloat(line.tax ?? '0') || 0;
  const base = qty * unitPrice;
  return base + (base * taxPct / 100);
}

/** Lignes remboursables — ordered, déjà remboursé, restant. */
export function refundLinesFromDetail(detail) {
  const rawItems = detail?.items?.length
    ? detail.items
    : (detail?.items_detail?.length ? detail.items_detail : []);
  return rawItems.map((it) => {
    const ordered = orderedQtyFromItem(it);
    const refunded = refundedQtyFromItem(it);
    const remaining = Math.max(0, ordered - refunded);
    const unitPrice = lineUnitPriceFromItem(it);
    return {
      ...it,
      key: fulfillmentLineKey(it),
      name: it.name ?? it.product_name ?? '—',
      ordered,
      refunded,
      remaining,
      pending: remaining > 0,
      unitPrice,
      tax: it.tax ?? '0.00',
      variantId: it.variant_id ?? it.variant ?? null,
    };
  });
}

export function computeRefundTotalFromQtys(lines, qtyByKey) {
  let total = 0;
  for (const line of lines ?? []) {
    const raw = parseInt(qtyByKey[line.key], 10);
    const qty = Number.isNaN(raw) ? 0 : Math.max(0, Math.min(raw, line.remaining));
    if (qty > 0) total += lineRefundAmount(line, qty);
  }
  return Math.round(total);
}

/** GET order detail for refund modal — read-only. */
export async function fetchOrderDetailForRefund(slug, orderId, channelSlug = 'web') {
  const res = await apiFetch(`/store/${slug}/orders/${orderId}/?format=json`);
  if (!res.ok) {
    throw new Error(await readOrderApiError(res, 'Impossible de charger la commande'));
  }
  const raw = await res.json();
  const items = raw.items_detail?.length ? raw.items_detail : (raw.items ?? []);
  const mapped = mapApiOrderToLocal({ ...raw, items_detail: items }, channelSlug);
  const refundLines = refundLinesFromDetail({ items });
  return { ...mapped, items, refundLines };
}

function orderItemPkFromLine(line) {
  const raw = line.item ?? line.id ?? line.pk ?? line.item_id;
  const num = Number(raw);
  if (Number.isFinite(num) && num > 0) return num;
  return raw;
}

/** Backend refund exige order.status = fulfilled (pas seulement fulfillement). */
async function ensureOrderStatusForRefund(slug, orderId, order) {
  const apiStatus = String(order?.apiOrderStatus ?? '').toLowerCase();
  const fulfillement = String(order?.fulfillement ?? '').toUpperCase();

  if (apiStatus === 'fulfilled') return;

  if (fulfillement !== 'FULFILLED') {
    throw new Error('Cette commande doit être expédiée pour être remboursée.');
  }

  // Annulée mais expédiée + payée — repasser temporairement en fulfilled pour POST /refunds/.
  if (apiStatus === 'canceled' || apiStatus === 'cancelled') {
    await saveApiOrder(slug, orderId, { id: orderId, status: 'fulfilled' });
    return;
  }

  if (apiStatus) {
    throw new Error(`Statut commande incompatible avec le remboursement (${apiStatus}).`);
  }
}

/**
 * Rembourse par quantité — POST /store/{slug}/refunds/ { order, refund_note, refund_items }.
 */
export async function refundApiOrderItems(slug, orderId, qtyToRefundByKey, preloadedDetail, order, refundNote = '') {
  const detail = preloadedDetail ?? await fetchOrderDetailForRefund(
    slug,
    orderId,
    order?.orderChannelSlug ?? 'web',
  );
  const lines = refundLinesFromDetail({ items: detail.items ?? detail.items_detail });
  const refundItems = [];
  let totalAmount = 0;

  for (const line of lines) {
    const raw = parseInt(qtyToRefundByKey[line.key], 10);
    const qty = Number.isNaN(raw) ? 0 : Math.max(0, Math.min(raw, line.remaining));
    if (qty <= 0) continue;

    totalAmount += lineRefundAmount(line, qty);
    refundItems.push({
      item: orderItemPkFromLine(line),
      quantity: qty,
    });
  }

  if (!refundItems.length) {
    throw new Error('Aucune quantité à rembourser.');
  }

  await ensureOrderStatusForRefund(slug, orderId, order);
  await createOrderRefund(slug, orderId, { refundNote, refundItems });

  return { amount: Math.round(totalAmount), lines: refundItems.length };
}

/** True when the order still has units to fulfill (stock deduction). */
export function orderHasPendingFulfillment(order) {
  const status = mapApiFulfilmentToPrepTab(order?.fulfillement);
  if (status === 'traite') return false;
  if (order?.fulfillmentLines?.length) {
    return order.fulfillmentLines.some((l) => l.pending);
  }
  return status === 'non_traite' || status === 'partiel';
}

async function readOrderApiError(res, fallback) {
  const text = await res.text().catch(() => '');
  if (!text) return `${fallback} (${res.status})`;
  try {
    const data = JSON.parse(text);
    if (data?.details) return String(data.details);
    if (data?.error && data?.details) return String(data.details);
    if (data?.detail) return String(data.detail);
    if (data?.error) return String(data.error);
    if (data?.message) return String(data.message);
  } catch { /* not JSON */ }
  if (/<!DOCTYPE|<html/i.test(text)) {
    if (res.status === 404) return `${fallback} (endpoint introuvable)`;
    return `${fallback} (${res.status})`;
  }
  const trimmed = text.trim();
  if (trimmed.length > 240) return `${fallback} (${res.status})`;
  return trimmed || `${fallback} (${res.status})`;
}

export async function fetchOrderFulfillmentsList(slug, orderId) {
  const res = await apiFetch(`/store/${slug}/orders/${orderId}/fulfillments/?format=json`);
  // API KIO : 404 = aucun dossier fulfillment (équivalent liste vide), pas une erreur.
  if (res.status === 404) return [];
  if (!res.ok) {
    throw new Error(await readOrderApiError(res, 'Impossible de charger les traitements'));
  }
  const data = await res.json();
  if (Array.isArray(data)) return data;
  return data.results ?? data.items ?? data.fulfillments ?? [];
}

export async function createOrderFulfillment(slug, orderId) {
  const res = await apiFetch(
    `/store/${slug}/orders/${orderId}/fulfillments/?format=json`,
    { method: 'POST', body: JSON.stringify({}) },
  );
  if (!res.ok) {
    throw new Error(await readOrderApiError(res, 'Impossible de créer le dossier de traitement'));
  }
  const data = await res.json();
  if (data && typeof data === 'object' && !Array.isArray(data) && isUuidLike(data.id)) {
    return data;
  }
  if (Array.isArray(data) && data[0]) return data[0];
  return null;
}

/** Resolve fulfillment folder id — POST only when saving, never on read. */
async function ensureOrderFulfillmentId(slug, orderId) {
  const list = await fetchOrderFulfillmentsList(slug, orderId);
  const existingId = list[0]?.id;
  if (isUuidLike(existingId)) return String(existingId).trim();

  const created = await createOrderFulfillment(slug, orderId);
  if (isUuidLike(created?.id)) return String(created.id).trim();

  throw new Error('Traitement impossible : identifiant fulfillments manquant.');
}

export async function fetchOrderFulfillmentLines(slug, orderId, fulfillmentId) {
  if (!isUuidLike(fulfillmentId)) return [];
  const res = await apiFetch(
    `/store/${slug}/orders/${orderId}/fulfillments/${fulfillmentId}/?format=json`,
  );
  // Dossier créé (POST {}) mais aucune ligne saisie → 404, traité comme vide.
  if (res.status === 404) return [];
  if (!res.ok) {
    throw new Error(await readOrderApiError(res, 'Impossible de charger le détail du traitement'));
  }
  const data = await res.json();
  return Array.isArray(data) ? data : (data.results ?? data.items ?? []);
}

function itemsWithFulfillmentFromOrder(rawItems, headerStatus) {
  return (rawItems ?? []).map((it) => {
    const ordered = orderedQtyFromItem(it);
    let fulfilled = fulfilledQtyFromItem(it);
    if (headerStatus === 'FULFILLED') fulfilled = ordered;
    if (headerStatus === 'UNFULFILLED') fulfilled = 0;
    return {
      ...it,
      ordered_quantity: ordered,
      new_fulfilled: fulfilled,
      fulfilled_quantity: fulfilled,
    };
  });
}

async function tryLoadFulfillmentRows(slug, orderId) {
  const list = await fetchOrderFulfillmentsList(slug, orderId);
  const fulfillmentId = list[0]?.id;
  if (!isUuidLike(fulfillmentId)) return [];
  return fetchOrderFulfillmentLines(slug, orderId, fulfillmentId);
}

function mergeFulfillmentIntoItems(items, fulfillmentRows, headerStatus) {
  const byItem = new Map();
  (fulfillmentRows ?? []).forEach((row) => {
    const itemRef = row.item ?? row.order_item;
    if (itemRef != null) byItem.set(Number(itemRef), row);
  });

  return items.map((it) => {
    const ordered = orderedQtyFromItem(it);
    let fulfilled = 0;
    if (headerStatus === 'FULFILLED') {
      fulfilled = ordered;
    } else if (headerStatus === 'UNFULFILLED') {
      fulfilled = 0;
    } else {
      const hit = byItem.get(Number(it.id));
      fulfilled = hit ? Number(hit.new_fulfilled ?? hit.quantity ?? 0) : fulfilledQtyFromItem(it);
    }
    return {
      ...it,
      ordered_quantity: ordered,
      new_fulfilled: fulfilled,
      fulfilled_quantity: fulfilled,
    };
  });
}

/** GET order for the stock treatment modal — read-only, never creates a fulfillment folder. */
export async function fetchOrderDetailForFulfillment(slug, orderId, channelSlug = 'web') {
  const res = await apiFetch(`/store/${slug}/orders/${orderId}/?format=json`);
  if (!res.ok) {
    throw new Error(await readOrderApiError(res, 'Impossible de charger la commande'));
  }
  const raw = await res.json();
  const headerStatus = String(raw.fulfillement ?? raw.shipping_status ?? 'UNFULFILLED').toUpperCase();
  let items = raw.items_detail?.length ? raw.items_detail : (raw.items ?? []);

  if (headerStatus === 'PARTIALLY_FULFILLED' || headerStatus === 'PARTIAL') {
    try {
      const rows = await tryLoadFulfillmentRows(slug, orderId);
      items = rows.length
        ? mergeFulfillmentIntoItems(items, rows, headerStatus)
        : itemsWithFulfillmentFromOrder(items, headerStatus);
    } catch {
      items = itemsWithFulfillmentFromOrder(items, headerStatus);
    }
  } else {
    items = itemsWithFulfillmentFromOrder(items, headerStatus);
  }

  const mapped = mapApiOrderToLocal({ ...raw, items_detail: items }, channelSlug);
  const fulfillmentLines = fulfillmentLinesFromDetail({ items });
  return { ...mapped, items, fulfillmentLines };
}

export async function postOrderFulfillmentBatch(slug, orderId, fulfillmentId, payload) {
  const res = await apiFetch(
    `/store/${slug}/orders/${orderId}/fulfillments/${fulfillmentId}/?format=json`,
    { method: 'POST', body: JSON.stringify(payload) },
  );
  if (!res.ok) {
    throw new Error(await readOrderApiError(res, 'Traitement stock impossible'));
  }
  try {
    return await res.json();
  } catch {
    return null;
  }
}

export async function putOrderFulfillmentLine(slug, orderId, fulfillmentId, payload) {
  const res = await apiFetch(
    `/store/${slug}/orders/${orderId}/fulfillments/${fulfillmentId}/?format=json`,
    { method: 'PUT', body: JSON.stringify(payload) },
  );
  if (!res.ok) {
    throw new Error(await readOrderApiError(res, 'Traitement stock impossible'));
  }
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Record stock fulfillment — POST/PUT on /fulfillments/{uuid}/.
 * @param {Object} [preloadedDetail] — data already loaded in the modal.
 */
export async function fulfillApiOrderItems(slug, orderId, qtyToFulfillByKey, preloadedDetail) {
  const hasInput = Object.values(qtyToFulfillByKey || {}).some((v) => (parseInt(v, 10) || 0) > 0);
  if (!hasInput) throw new Error('Aucune quantité à traiter.');

  const detail = preloadedDetail ?? await fetchOrderDetailForFulfillment(slug, orderId);
  const lines = fulfillmentLinesFromDetail({ items: detail.items ?? detail.items_detail });
  const fulfillmentId = await ensureOrderFulfillmentId(slug, orderId);

  const batch = [];
  const singles = [];

  for (const line of lines) {
    const key = fulfillmentLineKey(line);
    const remaining = line.remaining;
    const raw = parseInt(qtyToFulfillByKey[key], 10);
    const add = Number.isNaN(raw) ? 0 : Math.max(0, Math.min(raw, remaining));
    if (add <= 0) continue;

    const itemId = Number(line.id);
    if (!Number.isFinite(itemId)) continue;

    const alreadyFulfilled = line.fulfilled;
    if (alreadyFulfilled > 0) {
      singles.push({
        itemId,
        quantity: alreadyFulfilled + add,
        new_fulfilled: add,
      });
    } else {
      batch.push({ item: itemId, new_fulfilled: add, quantity: add });
    }
  }

  if (!batch.length && !singles.length) {
    throw new Error('Aucune quantité à traiter.');
  }

  if (batch.length) {
    await postOrderFulfillmentBatch(slug, orderId, fulfillmentId, batch);
  }

  if (singles.length) {
    const existingRows = await fetchOrderFulfillmentLines(slug, orderId, fulfillmentId);
    for (const s of singles) {
      let recordId;
      (existingRows ?? []).forEach(({ item, id }) => {
        if (String(item) === String(s.itemId)) recordId = id;
      });
      await putOrderFulfillmentLine(slug, orderId, fulfillmentId, {
        id: recordId,
        item: s.itemId,
        quantity: s.quantity,
        new_fulfilled: s.new_fulfilled,
      });
    }
  }
}

export { fetchStorePaymentMethods };

export function resolveOrderPaymentMethodId(methods, order) {
  const gatewayName = order?.paiement === 'cib' ? 'CIB Standard' : 'Cash';
  const match = (methods ?? []).find(
    (m) => String(m.gateway?.name ?? '').toLowerCase() === gatewayName.toLowerCase(),
  );
  if (match?.id) return Number(match.id);

  const cash = (methods ?? []).find((m) => m.gateway?.name === 'Cash');
  if (cash?.id) return Number(cash.id);

  const fallback = (methods ?? []).find((m) => m.gateway?.name !== 'CIB Standard');
  return fallback?.id ? Number(fallback.id) : null;
}

export async function fetchOrderPaymentsList(slug, orderId) {
  const res = await apiFetch(`/store/${slug}/orders/${orderId}/payments/?format=json`);
  if (res.status === 404) return [];
  if (!res.ok) {
    throw new Error(await readOrderApiError(res, 'Impossible de charger les paiements'));
  }
  const data = await res.json();
  if (Array.isArray(data)) return data;
  return data.results ?? data.payments ?? [];
}

function resolvePaymentMethodFromPayments(payments) {
  for (const row of payments ?? []) {
    const method = Number(row?.method);
    if (Number.isFinite(method) && method > 0) return method;
  }
  return null;
}

/** POST /store/{slug}/orders/{orderId}/payments/?format=json { method, amount } */
export async function createOrderPayment(slug, orderId, { method, amount }) {
  const value = Math.max(0, Number(amount) || 0);
  if (value <= 0) {
    throw new Error('Aucun montant à encaisser.');
  }
  const methodId = Number(method);
  if (!Number.isFinite(methodId) || methodId <= 0) {
    throw new Error('Méthode de paiement introuvable.');
  }

  const res = await apiFetch(
    `/store/${slug}/orders/${orderId}/payments/?format=json`,
    {
      method: 'POST',
      body: JSON.stringify({ method: methodId, amount: value }),
    },
  );
  if (!res.ok) {
    throw new Error(await readOrderApiError(res, 'Impossible d\'enregistrer le paiement'));
  }
  try {
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Enregistre un encaissement — GET payment-methods + payments existants, puis POST { method, amount }.
 * @param {number} amountToCollect — montant saisi (incrément, pas le cumul).
 */
export async function recordOrderPayment(slug, orderId, amountToCollect, order) {
  const value = Math.max(0, Number(amountToCollect) || 0);
  if (value <= 0) {
    throw new Error('Aucun montant à encaisser.');
  }

  const [methods, payments] = await Promise.all([
    fetchStorePaymentMethods(slug),
    fetchOrderPaymentsList(slug, orderId),
  ]);

  const method = resolvePaymentMethodFromPayments(payments)
    ?? resolveOrderPaymentMethodId(methods, order);

  if (!method) {
    throw new Error('Méthode de paiement introuvable pour cette boutique.');
  }

  return createOrderPayment(slug, orderId, { method, amount: value });
}

export function buildRefundsQuery({ page, pageSize, search = '' } = {}) {
  const params = new URLSearchParams();
  params.set('page', String(page ?? 1));
  if (pageSize != null) params.set('page_size', String(pageSize));
  if (search.trim()) params.set('search', search.trim());
  params.set('format', 'json');
  return params;
}

function refundProductsFromItems(refundItems) {
  return (refundItems ?? []).map((ri) => {
    const line = ri.item ?? ri;
    return {
      name: line.name ?? line.variant?.product_name ?? '—',
      qty: Number(ri.quantity) || 0,
      image: line.variant?.src ?? line.src ?? null,
    };
  });
}

function refundAmountFromApi(r) {
  const net = Number(r.net_total);
  if (Number.isFinite(net) && net >= 0) return Math.round(net);
  return Math.round((r.refund_items ?? []).reduce((s, ri) => {
    const lineTotal = Number(ri.net_total);
    if (Number.isFinite(lineTotal)) return s + lineTotal;
    const qty = Number(ri.quantity) || 0;
    const price = Number(ri.price ?? ri.item?.price ?? 0);
    return s + qty * price;
  }, 0));
}

/** Même logique que mapApiOrderToLocal — customer ou auxiliary.guest au niveau refund. */
function recipientFromRefundApi(r) {
  const guest = r.auxiliary?.guest;
  const customer = r.customer;
  const guestOrder = !customer
    && guest != null
    && typeof guest === 'object'
    && Object.keys(guest).length > 0;

  if (customer?.user) {
    return `${customer.user.first_name ?? ''} ${customer.user.last_name ?? ''}`.trim() || '—';
  }
  if (guestOrder && guest) {
    return `${guest.first_name ?? ''} ${guest.last_name ?? ''}`.trim() || 'Invité';
  }
  return 'Invité';
}

function refundTypeFromApi(r, refundAmount) {
  const items = r.refund_items ?? [];
  const partialLine = items.some((ri) => {
    const ordered = Number(ri.item?.quantity) || 0;
    const refunded = Number(ri.quantity) || 0;
    return ordered > 0 && refunded < ordered;
  });
  if (partialLine) return 'partial';
  const lineTotal = items.reduce((s, ri) => {
    const ordered = Number(ri.item?.quantity) || 0;
    const price = Number(ri.item?.price ?? 0);
    return s + ordered * price;
  }, 0);
  if (lineTotal > 0 && refundAmount < lineTotal * 0.99) return 'partial';
  return 'full';
}

/** Map GET /store/{slug}/refunds/ item → local list row. */
export function mapApiRefundToLocal(r) {
  const orderId = typeof r.order === 'string' || typeof r.order === 'number'
    ? String(r.order)
    : String(r.order?.id ?? '');
  const refundItems = r.refund_items ?? [];
  const products = refundProductsFromItems(refundItems);
  const refundAmount = refundAmountFromApi(r);
  const orderTotal = refundItems.reduce((s, ri) => {
    const ordered = Number(ri.item?.quantity) || 0;
    const price = Number(ri.item?.price ?? 0);
    return s + ordered * price;
  }, 0) || refundAmount;
  const guest = r.auxiliary?.guest;
  const guestOrder = !r.customer
    && guest != null
    && typeof guest === 'object'
    && Object.keys(guest).length > 0;

  return {
    id: String(r.id),
    refundName: r.sequence != null ? `#${r.sequence}` : '—',
    sequence: r.sequence ?? null,
    orderName: r.order_sequence != null ? `#${r.order_sequence}` : '—',
    orderSequence: r.order_sequence ?? null,
    orderId,
    recipient: recipientFromRefundApi(r),
    guest: guestOrder,
    phone: formatPhone(resolveOrderPhone(r)),
    products: products.length ? products : [{ name: '—', qty: 1, image: null }],
    orderTotal: Math.round(orderTotal),
    refundAmount,
    refundType: refundTypeFromApi(r, refundAmount),
    reason: 'autre',
    refundStatus: 'rembourse',
    paiement: 'cod',
    refundMethod: 'cod_reverse',
    canal: 'kiostore',
    requestedAt: r.created ?? null,
    processedAt: r.updated ?? r.created ?? null,
    note: r.refund_note ?? '',
    customerId: r.customer?.id ?? null,
    _api: true,
  };
}

export async function fetchRefundsPage(slug, { page = 1, pageSize = 10, search = '', signal } = {}) {
  const params = buildRefundsQuery({ page, pageSize, search });
  const res = await apiFetch(`/store/${slug}/refunds/?${params}`, { signal });
  if (res.status === 404) return { count: 0, next: null, previous: null, results: [] };
  if (!res.ok) {
    throw new Error(await readOrderApiError(res, 'Impossible de charger les remboursements'));
  }
  const data = await res.json();
  const raw = Array.isArray(data) ? data : (data.results ?? data.refunds ?? []);
  return {
    count: typeof data.count === 'number' ? data.count : raw.length,
    next: data.next ?? null,
    previous: data.previous ?? null,
    results: raw.map(mapApiRefundToLocal),
  };
}

export async function fetchOrderRefundsList(slug, orderId) {
  const params = new URLSearchParams({ format: 'json' });
  if (orderId) params.set('order', orderId);
  const res = await apiFetch(`/store/${slug}/refunds/?${params}`);
  if (res.status === 404) return [];
  if (!res.ok) {
    throw new Error(await readOrderApiError(res, 'Impossible de charger les remboursements'));
  }
  const data = await res.json();
  if (Array.isArray(data)) return data;
  return data.results ?? data.refunds ?? [];
}

/** POST /store/{slug}/refunds/?format=json { order, refund_note, refund_items } */
export async function createOrderRefund(slug, orderId, { refundNote = '', refundItems } = {}) {
  if (!refundItems?.length) {
    throw new Error('Aucune quantité à rembourser.');
  }

  const res = await apiFetch(
    `/store/${slug}/refunds/?format=json`,
    {
      method: 'POST',
      body: JSON.stringify({
        order: orderId,
        refund_note: refundNote ?? '',
        refund_items: refundItems,
      }),
    },
  );
  if (!res.ok) {
    throw new Error(await readOrderApiError(res, 'Impossible d\'enregistrer le remboursement'));
  }
  try {
    return await res.json();
  } catch {
    return null;
  }
}
